<template>
  <Head title="Edit Profile" />

  <div class="max-w-3xl mx-auto space-y-6 py-6 px-4 sm:px-6 lg:px-8">
      <!-- Page Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">Edit Profile</h1>
          <p class="mt-1 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-400">
            Update your personal information and preferences
          </p>
        </div>
        <Link :href="route('profile.show')">
          <Button variant="secondary">Cancel</Button>
        </Link>
      </div>

      <form @submit.prevent="submit" class="space-y-6">
        <!-- Profile Photo -->
        <Card>
          <template #header>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
              Profile Photo
            </h3>
          </template>
          <div class="p-6">
            <div class="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6">
              <Avatar
                :src="photoPreview || user.profile_photo_url"
                :name="user.full_name"
                size="xl"
                class="flex-shrink-0"
              />
              <div class="flex-1 space-y-3">
                <input
                  type="file"
                  ref="photoInput"
                  @change="handlePhotoChange"
                  accept="image/jpeg,image/png,image/gif"
                  class="hidden"
                />
                <div class="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    @click="$refs.photoInput.click()"
                    :disabled="uploadingPhoto"
                  >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {{ uploadingPhoto ? 'Uploading...' : 'Upload Photo' }}
                  </Button>
                  <Button
                    v-if="user.profile_photo_url"
                    type="button"
                    variant="danger"
                    @click="confirmDeletePhoto"
                    :disabled="deletingPhoto"
                  >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    {{ deletingPhoto ? 'Removing...' : 'Remove Photo' }}
                  </Button>
                </div>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  JPG, PNG or GIF. Maximum file size: 2MB.
                </p>
                <p v-if="photoError" class="text-sm text-red-600 dark:text-red-400">
                  {{ photoError }}
                </p>
              </div>
            </div>
          </div>
        </Card>

        <!-- Basic Information -->
        <Card>
          <template #header>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
              Basic Information
            </h3>
          </template>
          <div class="p-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                v-model="form.first_name"
                label="First Name"
                :error="form.errors.first_name"
                required
                placeholder="Enter your first name"
              />
              <Input
                v-model="form.last_name"
                label="Last Name"
                :error="form.errors.last_name"
                required
                placeholder="Enter your last name"
              />
              <Input
                v-model="form.phone"
                label="Phone Number"
                type="tel"
                :error="form.errors.phone"
                placeholder="+1 (555) 123-4567"
                class="md:col-span-2"
              />
            </div>
          </div>
        </Card>

        <!-- Preferences -->
        <Card>
          <template #header>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
              Preferences
            </h3>
          </template>
          <div class="p-6 space-y-6">
            <!-- Theme -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-3">
                Theme Preference
              </label>
              <div class="flex flex-col sm:flex-row gap-4">
                <label
                  class="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all flex-1"
                  :class="form.theme === 'light'
                    ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'"
                >
                  <input
                    type="radio"
                    v-model="form.theme"
                    value="light"
                    class="mr-3 text-blue-600 focus:ring-blue-500"
                  />
                  <svg class="w-5 h-5 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clip-rule="evenodd" />
                  </svg>
                  <span class="font-medium text-gray-900 dark:text-gray-100">Light</span>
                </label>
                <label
                  class="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all flex-1"
                  :class="form.theme === 'dark'
                    ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'"
                >
                  <input
                    type="radio"
                    v-model="form.theme"
                    value="dark"
                    class="mr-3 text-blue-600 focus:ring-blue-500"
                  />
                  <svg class="w-5 h-5 mr-2 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                  </svg>
                  <span class="font-medium text-gray-900 dark:text-gray-100">Dark</span>
                </label>
              </div>
              <p v-if="form.errors.theme" class="mt-1 text-sm text-red-600 dark:text-red-400">
                {{ form.errors.theme }}
              </p>
            </div>
          </div>
        </Card>

        <!-- Account Settings -->
        <Card>
          <template #header>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
              Account Settings
            </h3>
          </template>
          <div class="p-6 space-y-4">
            <!-- Email -->
            <div class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div class="flex-1">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                  Email Address
                </label>
                <p class="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  {{ user.email }}
                </p>
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Changing your email will require re-verification
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                @click="showEmailModal = true"
              >
                Change Email
              </Button>
            </div>
          </div>
        </Card>

        <!-- Submit Buttons -->
        <div class="flex flex-col sm:flex-row justify-end gap-3 pt-4">
          <Link :href="route('profile.show')">
            <Button type="button" variant="secondary" class="w-full sm:w-auto">
              Cancel
            </Button>
          </Link>
          <Button
            type="submit"
            :disabled="form.processing"
            class="w-full sm:w-auto"
          >
            <svg v-if="form.processing" class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ form.processing ? 'Saving...' : 'Save Changes' }}
          </Button>
        </div>
      </form>

      <!-- Email Change Modal -->
      <Modal :show="showEmailModal" @close="closeEmailModal" max-width="md">
        <template #header>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
            Change Email Address
          </h3>
        </template>
        <template #content>
          <form @submit.prevent="submitEmailChange" class="space-y-6">
            <div class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div class="flex">
                <svg class="h-5 w-5 text-yellow-600 dark:text-yellow-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                </svg>
                <div>
                  <h4 class="text-sm font-medium text-yellow-800 dark:text-yellow-200">Important Notice</h4>
                  <p class="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                    After changing your email, you'll need to verify the new address before you can sign in again.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
                Current Email
              </label>
              <p class="text-sm text-gray-900 dark:text-gray-100 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                {{ user.email }}
              </p>
            </div>

            <Input
              v-model="emailForm.email"
              label="New Email Address"
              type="email"
              :error="emailForm.errors.email"
              required
              placeholder="Enter new email address"
            />

            <Input
              v-model="emailForm.password"
              label="Current Password"
              type="password"
              :error="emailForm.errors.password"
              required
              placeholder="Confirm with your password"
              help="Enter your current password to confirm this change"
            />

            <div class="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="secondary"
                @click="closeEmailModal"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                :disabled="emailForm.processing"
              >
                {{ emailForm.processing ? 'Updating...' : 'Update Email' }}
              </Button>
            </div>
          </form>
        </template>
      </Modal>

      <!-- Delete Photo Confirmation Modal -->
      <Modal :show="showDeletePhotoModal" @close="showDeletePhotoModal = false" max-width="sm">
        <template #header>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
            Remove Profile Photo
          </h3>
        </template>
        <template #content>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            Are you sure you want to remove your profile photo? This action cannot be undone.
          </p>
          <div class="flex justify-end gap-3 mt-6">
            <Button
              type="button"
              variant="secondary"
              @click="showDeletePhotoModal = false"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              @click="deletePhoto"
              :disabled="deletingPhoto"
            >
              {{ deletingPhoto ? 'Removing...' : 'Remove Photo' }}
            </Button>
          </div>
        </template>
      </Modal>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useForm, router, Link, Head } from '@inertiajs/vue3';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Input from '@/Components/Common/Input.vue';
import Select from '@/Components/Common/Select.vue';
import Avatar from '@/Components/Common/Avatar.vue';
import Modal from '@/Components/Common/Modal.vue';
import { useToast } from '@/Composables/useToast';
import route from '@/Utils/route';

const props = defineProps({
  user: {
    type: Object,
    required: true,
  },
});

const { success, error } = useToast();
const photoInput = ref(null);
const photoPreview = ref(null);
const photoError = ref('');
const uploadingPhoto = ref(false);
const deletingPhoto = ref(false);
const showEmailModal = ref(false);
const showDeletePhotoModal = ref(false);

// Main profile form
const form = useForm({
  first_name: props.user.first_name || '',
  last_name: props.user.last_name || '',
  phone: props.user.phone || '',
  theme: props.user.theme || 'light',
});

// Email change form
const emailForm = useForm({
  email: '',
  password: '',
});

/**
 * Submit profile update form
 */
const submit = () => {
  form.put(route('profile.update'), {
    preserveScroll: false,
    onSuccess: () => {
      // Backend will show flash message, just redirect
      router.visit(route('profile.show'));
    },
    onError: (errors) => {
      error('Failed to update profile. Please check the form for errors.');
    },
  });
};

/**
 * Handle photo file selection
 */
const handlePhotoChange = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Validate file type
  const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
  if (!validTypes.includes(file.type)) {
    photoError.value = 'Please select a valid image file (JPG, PNG, or GIF)';
    return;
  }

  // Validate file size (2MB max)
  const maxSize = 2 * 1024 * 1024; // 2MB in bytes
  if (file.size > maxSize) {
    photoError.value = 'File size must be less than 2MB';
    return;
  }

  photoError.value = '';
  uploadPhoto(file);
};

/**
 * Upload photo to server
 */
const uploadPhoto = (file) => {
  uploadingPhoto.value = true;
  const formData = new FormData();
  formData.append('photo', file);

  // Create preview
  const reader = new FileReader();
  reader.onload = (e) => {
    photoPreview.value = e.target.result;
  };
  reader.readAsDataURL(file);

  router.post(route('profile.photo.upload'), formData, {
    preserveScroll: true,
    onSuccess: (page) => {
      uploadingPhoto.value = false;
      photoPreview.value = null;
      success('Profile photo uploaded successfully');
    },
    onError: (errors) => {
      uploadingPhoto.value = false;
      photoPreview.value = null;
      photoError.value = errors.photo || 'Failed to upload photo';
      error('Failed to upload photo');
    },
    onFinish: () => {
      // Reset file input
      if (photoInput.value) {
        photoInput.value.value = '';
      }
    },
  });
};

/**
 * Confirm photo deletion
 */
const confirmDeletePhoto = () => {
  showDeletePhotoModal.value = true;
};

/**
 * Delete profile photo
 */
const deletePhoto = () => {
  deletingPhoto.value = true;
  router.delete(route('profile.photo.delete'), {
    preserveScroll: true,
    onSuccess: () => {
      deletingPhoto.value = false;
      showDeletePhotoModal.value = false;
      photoPreview.value = null;
      success('Profile photo removed successfully');
    },
    onError: () => {
      deletingPhoto.value = false;
      error('Failed to remove photo');
    },
  });
};

/**
 * Submit email change
 */
const submitEmailChange = () => {
  emailForm.put(route('profile.email.update'), {
    preserveScroll: true,
    onSuccess: () => {
      closeEmailModal();
      success('Email updated successfully. Please check your inbox to verify your new email address.');
    },
    onError: (errors) => {
      if (errors.password) {
        error('Invalid password');
      } else {
        error('Failed to update email');
      }
    },
  });
};

/**
 * Close email modal and reset form
 */
const closeEmailModal = () => {
  showEmailModal.value = false;
  emailForm.reset();
  emailForm.clearErrors();
};

/**
 * Format relative date (e.g., "2 days ago")
 */
const formatRelativeDate = (dateString) => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)} weeks ago`;
  if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;
  return `${Math.floor(diffInSeconds / 31536000)} years ago`;
};
</script>
